import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import moment from "moment";
import { GridRowModes, DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import { db_readchain } from "../../../firebase";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDocs,
  query,
  where
} from "firebase/firestore";

const EditToolbar = React.memo(function EditToolbar(props) {
  const { rows, isEditing } = props;
  const handleClick = () => {
    const id = rows.length ? rows.length + 1 : 1;
    const currentDate = new Date();
    props.setIsEditing(true);
    const newRow = {
      id,
      characters: "",
      words: "",
      sentences: "",
      recordedAt: currentDate,
      isNew: true
    };

    props.setRows(oldRows => {
      return [newRow].concat(oldRows);
    });

    props.setRowModesModel(oldModel => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "characters" }
    }));
  };

  return (
    <GridToolbarContainer>
      <Button
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleClick}
        disabled={isEditing}
      >
        Add record
      </Button>
    </GridToolbarContainer>
  );
});

const ProprietaryTestsGrid = React.memo(function ProprietaryTestsGrid({
  data,
  email,
  userId
}) {
  const [rows, setRows] = React.useState([]);
  const [isEditing, setIsEditing] = React.useState(false);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [openDialog, setOpenDialog] = React.useState(false);
  const [rowToDelete, setRowToDelete] = React.useState(null);
  React.useEffect(() => {
    if (data) {
      const transformedData = data.map((item, index) => ({
        docId: item.testDocId,
        id: index + 1,
        characters: item.letters_score,
        words: item.words_score,
        sentences: item.sentences_score,
        recordedAt: new Date(item.date)
      }));
      setRows(transformedData);
    }
  }, [data]);

  const handleRowEditStop = async (params, event) => {
    // if (
    //   params.reason === GridRowEditStopReasons.rowFocusOut ||
    //   params.reason === GridRowEditStopReasons.enterKeyDown
    // ) {
    setIsEditing(false);
  };

  const saveRowToFirebase = async rowData => {
    const usersTestsRef = collection(db_readchain, "UsersTests");
    const userTestQuery = query(usersTestsRef, where("email", "==", email));
    const userTestSnapshot = await getDocs(userTestQuery);
    if (userTestSnapshot.empty) {
      console.error("No matching user found.");
      return;
    }

    const userDoc = userTestSnapshot.docs[0];
    const testsCollectionRef = collection(userDoc.ref, "Tests");
    const testTypes = ["characters", "words", "sentences"];
    const testTypeToKeyMapping = {
      characters: "letters",
      words: "words",
      sentences: "sentences"
    };

    const docIds = []; // Array to hold the doc IDs

    // calculate recorded date as start of day in utc
    const timezoneOffset = moment(rowData.recordedAt).utcOffset();
    let updatedDate = moment(rowData.recordedAt)
      .utc()
      .add(timezoneOffset, "minutes")
      .startOf("day")
      .toDate();

    for (const testType of testTypes) {
      const newTestRef = doc(testsCollectionRef);
      const firebaseData = {
        score: rowData[testType],
        test_Type: testTypeToKeyMapping[testType],
        date: updatedDate
      };

      try {
        await setDoc(newTestRef, firebaseData);
        docIds.push(newTestRef.id); // Push each doc ID to the array
      } catch (e) {
        console.log(e);
      }
    }

    return {
      ...rowData,
      docId: docIds,
      isNew: false
    };
  };
  const handleProcessRowUpdateError = error => {};

  const handleCellEditCommit = async (params, event) => {
    const { id, field, value } = params;
    // Update local state
    setRows(prevRows =>
      prevRows.map(row =>
        row.id === id
          ? {
              ...row,
              [field]: value
            }
          : row
      )
    );
  };

  const processRowUpdate = async editedRow => {
    const isValidData = ["characters", "words", "sentences"].some(
      field => !!editedRow[field]
    );
    if (!isValidData && editedRow.isNew) {
      setRows(prevRows => prevRows.filter(row => row.id !== editedRow.id));
      return {
        ...editedRow
      };
    }
    if (isValidData) {
      // If it's a new row, save to Firebase
      if (editedRow.isNew) {
        editedRow = await saveRowToFirebase(editedRow);
        setRows(prevRows =>
          prevRows.map(row => (row.id === editedRow.id ? editedRow : row))
        );
      } else {
        await updateRowInFirebase(editedRow.docId, editedRow);
      }
      setRows(prevRows =>
        prevRows.map(row => (row.id === editedRow.id ? editedRow : row))
      );
      return {
        ...editedRow,
        isNew: false
      };
    } else {
      // If no valid data and it's a new row, remove it from the state
      if (editedRow.isNew) {
        setRows(prevRows => prevRows.filter(row => row.id !== editedRow.id));
      }
      return {
        ...editedRow
      };
    }
  };

  const updateRowInFirebase = async (ids, rowData) => {
    const usersTestsRef = collection(db_readchain, "UsersTests");
    const userTestQuery = query(usersTestsRef, where("email", "==", email));
    const userTestSnapshot = await getDocs(userTestQuery);

    if (userTestSnapshot.empty) {
      console.error("No matching user found.");
      return;
    }

    const userDoc = userTestSnapshot.docs[0];
    const testsCollectionRef = collection(userDoc.ref, "Tests");

    const testTypes = ["characters", "words", "sentences"];
    const testTypeToKeyMapping = {
      characters: "letters",
      words: "words",
      sentences: "sentences"
    };
    const timezoneOffset = moment(rowData.recordedAt).utcOffset();
    let updatedDate = moment(rowData.recordedAt)
      .utc()
      .add(timezoneOffset, "minutes")
      .startOf("day")
      .toDate();

    for (let i = 0; i < ids.length; i++) {
      const testDocRef = doc(testsCollectionRef, ids[i]);

      const firebaseData = {
        score: rowData[testTypes[i]],
        test_Type: testTypeToKeyMapping[testTypes[i]],
        date: updatedDate
      };

      try {
        await updateDoc(testDocRef, firebaseData);
      } catch (e) {
        console.log(e);
      }
    }

    return {
      ...rowData,
      docId: ids, // Return the array of docIds
      isNew: false
    };
  };

  const handleRowModesModelChange = newRowModesModel => {
    setRowModesModel(newRowModesModel);
  };
  const numberInputOnWheelPreventChange = e => {
    e.target.blur();

    e.stopPropagation();

    setTimeout(() => {
      e.target.focus();
    }, 0);
  };
  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.5,
      align: "center",
      headerAlign: "center"
    },
    {
      field: "characters",
      headerName: "Characters",
      flex: 1,
      editable: true,
      align: "center",
      type: "number",
      headerAlign: "center",
      renderCell: params => (
        <input
          type="number"
          value={params.value}
          onWheel={numberInputOnWheelPreventChange}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            height: "100%",
            textAlign: "center",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: "inherit",
            color: "inherit"
          }}
        />
      ),
      headerAlign: "center"
    },
    {
      field: "words",
      headerName: "Words",
      flex: 1,
      editable: true,
      align: "center",
      renderCell: params => (
        <input
          type="number"
          value={params.value}
          onWheel={numberInputOnWheelPreventChange}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            height: "100%",
            textAlign: "center",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: "inherit",
            color: "inherit"
          }}
        />
      ),
      headerAlign: "center"
    },
    {
      field: "sentences",
      headerName: "Sentences",
      flex: 1,
      editable: true,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: params => (
        <input
          type="number"
          value={params.value}
          onWheel={numberInputOnWheelPreventChange}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            height: "100%",
            textAlign: "center",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: "inherit",
            color: "inherit"
          }}
        />
      ),
      headerAlign: "center"
    },
    {
      field: "recordedAt",
      headerName: "Recorded At",
      flex: 1,
      align: "center",
      headerAlign: "center",
      type: "date",
      editable: true
    },
    {
      field: "delete",
      headerName: "Delete",
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      renderCell: params => (
        <Button color="secondary" onClick={() => initiateDelete(params.row)}>
          Delete
        </Button>
      ),
      sortable: false,
      filterable: false,
      disableColumnMenu: true
    }
  ];

  const initiateDelete = id => {
    setRowToDelete(id.id);
    setOpenDialog(true);
  };

  const confirmDelete = async () => {
    if (rowToDelete) {
      try {
        const row = rows.find(r => r.id === rowToDelete);
        if (row && row.docId && row.docId.length > 0) {
          const usersTestsRef = collection(db_readchain, "UsersTests");
          const userTestQuery = query(
            usersTestsRef,
            where("email", "==", email)
          );
          const userTestSnapshot = await getDocs(userTestQuery);

          if (userTestSnapshot.empty) {
            console.error("No matching user found.");
            return;
          }

          const userDoc = userTestSnapshot.docs[0];
          const testsCollectionRef = collection(userDoc.ref, "Tests");

          for (const docId of row.docId) {
            const testDocRef = doc(testsCollectionRef, docId);
            await updateDoc(testDocRef, { deleted: true });
          }
        }

        setRows(prevRows => prevRows.filter(row => row.id !== rowToDelete));

        setRowToDelete(null);
      } catch (error) {
        console.error("Error updating records to set deleted flag: ", error);
      }
    }

    // Close the confirmation dialog
    setOpenDialog(false);
  };

  const cancelDelete = () => {
    setRowToDelete(null);
    setOpenDialog(false);
  };
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        height: 350,
        width: "100%",
        "& .actions": {
          color: "text.secondary"
        },
        "& .textPrimary": {
          color: "text.primary"
        }
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        sx={{
          ".MuiDataGrid-selectedRowCount": {
            visibility: "hidden"
          }
        }}
        onEditCellChangeCommitted={handleCellEditCommit}
        pageSizeOptions={[5, 10, 15]}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: "id",
                sort: "desc"
              }
            ]
          }
        }}
        slots={{
          toolbar: EditToolbar
        }}
        slotProps={{
          toolbar: {
            setRows,
            setRowModesModel,
            rows,
            saveRowToFirebase,
            isEditing,
            setIsEditing
          }
        }}
      />
      <Dialog
        open={openDialog}
        onClose={cancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default ProprietaryTestsGrid;
